import { useEffect, useState } from 'react'

import { getCreditProfiles } from 'api/application'
import AssetType from 'state/enums/AssetType'

import useUser from './useUser'

const useSafeCheck = (
    applicationUuid: string | undefined,
    applicationAssetType: AssetType | undefined,
) => {
    const { user } = useUser()
    const [canUseSafeCheck, setCanUseSafeCheck] = useState<boolean>(false)
    const [canEnroll, setCanEnroll] = useState<boolean>(false)
    const [hasSafeCheckReport, setHasSafeCheckReport] = useState<boolean>(false)
    const [safeCheckVisible, setSafeCheckVisible] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(true)

    useEffect(() => {
        const hasSafeCheckEnabled = user?.dealership?.isSafeCheckEnabled
        const hasPrequalEnabled = user?.dealership?.isPrequalEnabled
        const isIndividualUser = user?.actualNumberOfEndUsers === 1

        setSafeCheckVisible(
            // The terms of our contract with Experian
            // prohibit us from promoting SafeCheck in
            // the context of AUTOMOBILE applications
            applicationAssetType !== AssetType.AUTOMOBILE &&
                Boolean(
                    isIndividualUser &&
                    (hasPrequalEnabled || hasSafeCheckEnabled),
                ),
        )

        setCanUseSafeCheck(
            Boolean(
                isIndividualUser && hasPrequalEnabled && hasSafeCheckEnabled,
            ),
        )

        setCanEnroll(
            Boolean(
                isIndividualUser && hasPrequalEnabled && !hasSafeCheckEnabled,
            ),
        )
    }, [user, applicationAssetType])

    useEffect(() => {
        if (!canUseSafeCheck || !applicationUuid || !applicationAssetType) {
            setIsLoading(false)

            return
        }

        setIsLoading(true)

        getCreditProfiles(applicationUuid)
            .then((creditProfiles) => {
                const hasSafeCheckSoftPull = Boolean(
                    creditProfiles.find(
                        (creditProfile) =>
                            creditProfile.creditAccessId &&
                            creditProfile.pullType === 'safecheck_soft_pull',
                    ),
                )

                setHasSafeCheckReport(hasSafeCheckSoftPull)
            })
            .finally(() => {
                setIsLoading(false)
            })
    }, [canUseSafeCheck, applicationUuid, applicationAssetType])

    return {
        canUseSafeCheck,
        canEnroll,
        hasSafeCheckReport,
        safeCheckVisible,
        isLoading,
    }
}

export default useSafeCheck
